import { useState, useEffect } from 'react';
import { Box, Typography, Container, Button, useTheme } from '@mui/material';
import SliderHeroImage2 from '../../assets/sliderHero/sliderHeroImage2.svg';
import SliderHeroImage4 from '../../assets/sliderHero/sliderHeroImage4.svg';
import SliderHeroImage6 from '../../assets/sliderHero/sliderHeroImage6.svg';
import SliderHeroImage8 from '../../assets/sliderHero/sliderHeroImage8.svg';
import SliderHeroImage10 from '../../assets/sliderHero/sliderHeroImage10.svg';

const heroImages = [
  SliderHeroImage10,
  SliderHeroImage2,
  SliderHeroImage8,
  SliderHeroImage4,
  SliderHeroImage6,
];

export const LPSectionHero = () => {
  const theme = useTheme();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentImageIndex(prevIndex => (prevIndex + 1) % heroImages.length);
        setFade(true);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '88vh',
        overflow: 'hidden',
        position: 'relative',
        [theme.breakpoints.down('md')]: {
          height: 'auto',
          minHeight: '70vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'stretch',
          py: 6,
        }
      }}
    >
      {/* Background Image with Fade Effect */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          backgroundImage: `url(${heroImages[currentImageIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'opacity 0.8s ease-in-out',
          opacity: fade ? 1 : 0,
          zIndex: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: {
              xs: 'rgba(0, 0, 0, 0.78)',
              md: 'linear-gradient(50deg, rgba(0, 0, 0, 0.85) 30%, transparent 80%)',
            },
            pointerEvents: 'none'
          }
        }}
      />

      {/* Foreground Content */}
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          height: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'flex-end',
          [theme.breakpoints.down('md')]: {
            height: 'auto',
            minHeight: '70vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: { xs: '100%', md: '580px' },
            gap: '1rem',
            marginBottom: '6rem',
            px: { xs: 2, md: 2 },
            [theme.breakpoints.down('md')]: {
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '0',
            }
          }}
        >
          {/* Eyebrow */}
          <Typography
            fontFamily="Inter"
            sx={{
              fontSize: { xs: '11px', md: '12px' },
              fontWeight: 600,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: '#0CAECF',
              [theme.breakpoints.down('md')]: { textAlign: 'center' }
            }}
          >
            TikTok · Meta · Scaled Creator Content Engine
          </Typography>

          {/* Headline */}
          <Typography
            variant="h2"
            component="h1"
            fontFamily="Inter"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2.2rem', md: '44px' },
              lineHeight: '115%',
              letterSpacing: '0px',
              color: '#ffffff',
              [theme.breakpoints.down('md')]: {
                textAlign: 'center',
                fontSize: '24px',
                lineHeight: '120%',
              }
            }}
          >
            Scale your TikTok and Meta UGC pipeline. Hundreds of creator videos a month, attributed per creator.
          </Typography>

          {/* Sub-headline */}
          <Typography
            fontFamily="Inter"
            sx={{
              fontWeight: 300,
              fontSize: { xs: '14px', md: '16px' },
              lineHeight: '150%',
              color: '#C0C0C0',
              maxWidth: '520px',
              [theme.breakpoints.down('md')]: { textAlign: 'center' }
            }}
          >
            Built for growth-stage mobile app teams. We run the whole stack — creator sourcing, warmed-up account network, viral-testing playbook, and per-creator MMP attribution — so your in-house team shrinks to approvals.
          </Typography>

          {/* Trust-signal strip */}
          <Typography
            fontFamily="Inter"
            sx={{
              fontSize: { xs: '11px', md: '12px' },
              color: '#808080',
              letterSpacing: { xs: 0, md: '0.5px' },
              textAlign: { xs: 'center', md: 'left' },
              alignSelf: { xs: 'center', md: 'flex-start' },
              maxWidth: '100%'
            }}
          >
            Public pricing · No annual contract · No hidden creator fees
          </Typography>

          {/* CTA */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: { xs: 'center', md: 'flex-start' } }}>
            <Button
              component="a"
              href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                maxWidth: '240px',
                px: 3,
                py: 1.5,
                borderRadius: '170px',
                boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
                fontWeight: 700,
                fontSize: '15px',
                background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(163deg, rgb(12, 160, 200) -29.1%, rgb(80, 10, 100) 96.45%)'
                }
              }}
            >
              Book a Discovery Call →
            </Button>
            <Typography fontFamily="Inter" sx={{ fontSize: '12px', color: '#808080' }}>
              30-minute call. No pitch. See if we&apos;re the right fit.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
